/**
 * AlcoaResultsView - ALCOA+ Regulatory Compliance Results Page
 * ============================================================================
 * DC-520: Full ALCOA+ findings view with export capabilities
 *
 * Displays:
 * - Journey/asset header with compliance status
 * - Full findings panel with filtering
 * - Export buttons (PDF/JSON)
 * - Navigation back to Strategic dashboard
 *
 * @version 1.0.0
 * @created 2025-12-20
 * ============================================================================
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { AlcoaFindingsPanel, FindingSeverity } from '../../components/regulatory/AlcoaFindingsPanel';
import { AlcoaSummaryCard } from '../../components/regulatory/AlcoaSummaryCard';
import { useApiClient } from '../../hooks/useApiClient';
import '../../styles/journey.css';

// ============================================================================
// Types
// ============================================================================

interface JourneyInfo {
  group_id: string;
  name: string;
  description?: string;
}

// ============================================================================
// Main Component
// ============================================================================

export const AlcoaResultsView: React.FC = () => {
  const { group_id } = useParams<{ group_id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { authFetch } = useApiClient();

  const [journey, setJourney] = useState<JourneyInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState<'pdf' | 'json' | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  // Get initial filter from URL params
  const initialSeverity = searchParams.get('severity');
  // const assessmentId = searchParams.get('assessment'); // Reserved for future use

  // Parse severity filter from URL
  const initialSeverityFilter: FindingSeverity[] = initialSeverity
    ? (initialSeverity.split(',') as FindingSeverity[])
    : [];

  // Fetch journey info
  useEffect(() => {
    if (group_id) {
      fetchJourneyInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only fetch on group_id change
  }, [group_id]);

  const fetchJourneyInfo = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await authFetch(`/api/journey/${group_id}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch journey: ${response.statusText}`);
      }

      const data = await response.json();
      setJourney(data.journey || data);
      setLoading(false);
    } catch (err: unknown) {
      console.error('[AlcoaResultsView] Fetch error:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      setLoading(false);
    }
  };

  const handleExport = async (format: 'pdf' | 'json') => {
    if (!group_id) return;

    try {
      setExporting(format);
      setExportError(null);

      // Build export URL
      const exportUrl = `/api/v1/journeys/${group_id}/alcoa/export?format=${format}`;

      const response = await authFetch(exportUrl);

      if (!response.ok) {
        throw new Error(`Export failed: ${response.statusText}`);
      }

      // Download the file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `alcoa-assessment-${group_id}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: unknown) {
      console.error('[AlcoaResultsView] Export error:', err);
      setExportError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setExporting(null);
    }
  };

  const handleNavigateToStation = (_stationId: string) => {
    // Navigate to the stations tab with the specific station highlighted
    navigate(`/journey/${group_id}#stations`);
  };

  // Loading state
  if (loading) {
    return (
      <div className="alcoa-results-view">
        <div className="loading-state">
          <div className="spinner" />
          <p>Loading ALCOA+ assessment...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !journey) {
    return (
      <div className="alcoa-results-view">
        <div className="error-state">
          <div className="error-icon">⚠️</div>
          <h3>Failed to Load Assessment</h3>
          <p>{error || 'Journey not found'}</p>
          <button className="btn btn-secondary" onClick={() => navigate('/journey')}>
            ← Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="alcoa-results-view">
      {/* Header */}
      <div className="alcoa-results-header">
        <div className="header-breadcrumb">
          <button className="breadcrumb-link" onClick={() => navigate('/journey')}>
            Journeys
          </button>
          <span className="breadcrumb-separator">/</span>
          <button className="breadcrumb-link" onClick={() => navigate(`/journey/${group_id}`)}>
            {journey.name}
          </button>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">ALCOA+ Compliance</span>
        </div>

        <div className="header-title-row">
          <div className="header-left">
            <h1>ALCOA+ Regulatory Compliance</h1>
            <p className="subtitle">21 CFR Part 11 Data Integrity Assessment</p>
          </div>

          <div className="header-actions">
            <button
              className="btn btn-secondary"
              onClick={() => handleExport('json')}
              disabled={exporting !== null}
            >
              {exporting === 'json' ? 'Exporting...' : 'Export JSON'}
            </button>
            <button
              className="btn btn-primary"
              onClick={() => handleExport('pdf')}
              disabled={exporting !== null}
            >
              {exporting === 'pdf' ? 'Exporting...' : 'Export PDF'}
            </button>
          </div>
        </div>

        {exportError && (
          <div className="error-banner" role="alert">
            <strong>Export failed.</strong>
            <p>{exportError} — try again, or switch formats (JSON exports are lighter than PDF).</p>
            <button className="btn btn-secondary btn-sm" onClick={() => setExportError(null)}>Dismiss</button>
          </div>
        )}
      </div>

      {/* Content Grid */}
      <div className="alcoa-results-content">
        {/* Summary Card (Sidebar) */}
        <aside className="alcoa-results-sidebar">
          <AlcoaSummaryCard
            journeyId={group_id || ''}
            compact={false}
          />
        </aside>

        {/* Findings Panel (Main) */}
        <main className="alcoa-results-main">
          <AlcoaFindingsPanel
            journeyId={group_id || ''}
            initialSeverityFilter={initialSeverityFilter}
            onNavigateToStation={handleNavigateToStation}
            onRemediationAction={(finding, action) => {
              console.log('[AlcoaResultsView] Remediation action:', finding.findingCode, action);
              // TODO: Implement remediation actions (AI assist, downloads, etc.)
            }}
          />
        </main>
      </div>

      {/* Back to Strategic View */}
      <div className="alcoa-results-footer">
        <button
          className="btn btn-secondary"
          onClick={() => navigate(`/journey/${group_id}`)}
        >
          ← Back to Strategic View
        </button>
      </div>

      {/* Inline Styles for this view — cream/teal tokens (v2.0 beat 8).
          The old "Dark Mode Overrides for ALCOA Findings Panel" section is
          gone: alcoa-findings-panel.css is cream-native now, so re-painting
          it here would double-skin the panel. */}
      <style>{`
        .alcoa-results-view {
          min-height: 100vh;
          background: var(--tri-cream);
          padding: 1.5rem;
          color: var(--tri-body);
          font-family: var(--tri-font);
        }

        .alcoa-results-header {
          margin-bottom: 1.5rem;
        }

        .header-breadcrumb {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.875rem;
          color: var(--tri-slate);
          margin-bottom: 1rem;
        }

        .breadcrumb-link {
          background: none;
          border: none;
          color: var(--tri-teal);
          font-weight: 600;
          cursor: pointer;
          padding: 0;
        }

        .breadcrumb-link:hover {
          text-decoration: underline;
        }

        .breadcrumb-separator {
          color: var(--tri-taupe);
        }

        .breadcrumb-current {
          color: var(--tri-ink);
        }

        .header-title-row {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .header-left h1 {
          font-size: 1.75rem;
          font-weight: 600;
          color: var(--tri-teal);
          margin: 0 0 0.25rem;
        }

        .header-left .subtitle {
          font-size: 0.95rem;
          color: var(--tri-slate);
          margin: 0;
        }

        .header-actions {
          display: flex;
          gap: 0.75rem;
        }

        .alcoa-results-content {
          display: grid;
          grid-template-columns: 340px 1fr;
          gap: 1.5rem;
          margin-bottom: 1.5rem;
        }

        @media (max-width: 1024px) {
          .alcoa-results-content {
            grid-template-columns: 1fr;
          }
        }

        .alcoa-results-sidebar {
          position: sticky;
          top: 1.5rem;
          height: fit-content;
        }

        .alcoa-results-main {
          min-width: 0;
        }

        .alcoa-results-footer {
          padding-top: 1rem;
          border-top: 1px solid var(--tri-taupe);
        }

        /* Button styles */
        .btn {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.625rem 1rem;
          border-radius: 6px;
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s ease;
        }

        .btn-primary {
          background: var(--tri-terra);
          color: #fff;
          border: none;
        }

        .btn-primary:hover:not(:disabled) {
          background: var(--tri-terra-dk);
        }

        .btn-secondary {
          background: transparent;
          color: var(--tri-teal);
          border: 1px solid var(--tri-taupe);
        }

        .btn-secondary:hover:not(:disabled) {
          background: var(--tri-card-2);
        }

        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .loading-state,
        .error-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 60vh;
          text-align: center;
        }

        .error-icon {
          font-size: 3rem;
          margin-bottom: 1rem;
        }

        .spinner {
          width: 40px;
          height: 40px;
          border: 3px solid var(--tri-track);
          border-top-color: var(--tri-teal);
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 1rem;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AlcoaResultsView;
